/**
 * Sports Realm — product page variant behaviour.
 *
 * Deliberately small. It does not touch the cart: add-to-cart stays with
 * Horizon's <product-form-component>, which reads the hidden `id` input this
 * script keeps in sync. Everything it needs comes from a JSON blob of the real
 * product variants rendered by the section — no ids, prices or URLs in JS.
 *
 * Responsibilities:
 *   1. match the checked option values to a real variant
 *   2. keep the hidden variant id, price, sale flag and stock line in sync
 *   3. switch the gallery to the variant's own media
 *   4. mark option values that don't exist for the current colour
 *   5. keep ?variant= in the address bar so the URL is shareable
 */
(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-sr-pdp]');
    if (!root) return;

    var dataEl = root.querySelector('[data-sr-variants]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return; // leave the server-rendered state alone rather than half-working
    }

    var idInput = root.querySelector('[data-sr-variant-id]');
    var priceCurrent = root.querySelector('[data-sr-current]');
    var priceCompare = root.querySelector('[data-sr-compare]');
    var priceEl = root.querySelector('[data-sr-price]');
    var saveFlag = root.querySelector('[data-sr-saveflag]');
    var stockEl = root.querySelector('[data-sr-stock]');
    var colourLabel = root.querySelector('[data-sr-colour-label]');
    var atc = root.querySelector('.sr-pdp__atc');
    var saveAmt = root.querySelector('[data-sr-saveamt]');
    var buybar = root.querySelector('[data-sr-buybar]');
    var buybarPrice = root.querySelector('[data-sr-buybar-price]');
    var buybarAdd = root.querySelector('[data-sr-buybar-add]');
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-sr-optgroup]'));

    function selectedValues() {
      // Index by option position so option order in the admin never matters.
      var out = [];
      groups.forEach(function (g) {
        var pos = parseInt(g.getAttribute('data-option-position'), 10);
        var checked = g.querySelector('input:checked');
        if (checked) out[pos - 1] = checked.value;
      });
      return out;
    }

    function findVariant(values) {
      return variants.find(function (v) {
        return v.options.every(function (o, i) {
          return values[i] === undefined || o === values[i];
        });
      });
    }

    function setMedia(mediaId) {
      if (!mediaId) return;
      var slide = root.querySelector('[data-sr-slide][data-media-id="' + mediaId + '"]');
      if (!slide) return;
      root.querySelectorAll('[data-sr-slide]').forEach(function (s) {
        var on = s === slide;
        s.classList.toggle('is-active', on);
        if (on) s.removeAttribute('aria-hidden');
        else s.setAttribute('aria-hidden', 'true');
        // Never leave a hidden video playing.
        var vid = s.querySelector('video');
        if (vid && !on && !vid.paused) vid.pause();
      });
      root.querySelectorAll('[data-sr-thumb]').forEach(function (t) {
        var on = t.getAttribute('data-media-id') === String(mediaId);
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
    }

    /** Mark values that produce no variant for the other chosen options. */
    function markAvailability() {
      var values = selectedValues();
      groups.forEach(function (g) {
        var pos = parseInt(g.getAttribute('data-option-position'), 10);
        g.querySelectorAll('input[data-sr-option]').forEach(function (input) {
          var probe = values.slice();
          probe[pos - 1] = input.value;
          var match = variants.filter(function (v) {
            return v.options.every(function (o, i) {
              return probe[i] === undefined || o === probe[i];
            });
          });
          var any = match.some(function (v) { return v.available; });
          var label = input.closest('label');
          if (label) label.classList.toggle('is-unavailable', !any);
        });
      });
    }

    function syncActiveClasses() {
      root.querySelectorAll('[data-sr-optgroup] label').forEach(function (label) {
        var input = label.querySelector('input');
        if (!input) return;
        label.classList.toggle('is-active', input.checked);
        // Colour swatches share the homepage component, which marks the active
        // state with .sr-is-active rather than .is-active.
        if (label.classList.contains('sr-cw__sw')) {
          label.classList.toggle('sr-is-active', input.checked);
        }
      });
      // The dropdown's closed state has to show what is selected.
      root.querySelectorAll('[data-sr-select]').forEach(function (sel) {
        var checked = sel.querySelector('input:checked');
        var out = sel.querySelector('[data-sr-select-value]');
        if (checked && out) out.textContent = checked.value;
      });
    }

    function update() {
      var values = selectedValues();
      var variant = findVariant(values);

      syncActiveClasses();
      markAvailability();

      if (colourLabel) {
        // Matches the homepage swatch container class.
        var colourGroup = root.querySelector('.sr-cw__swatches');
        var checkedColour = colourGroup && colourGroup.querySelector('input:checked');
        if (checkedColour) colourLabel.textContent = checkedColour.value;
      }

      if (!variant) {
        // A combination the merchant never created — block the sale honestly.
        if (idInput) idInput.value = '';
        if (atc) atc.disabled = true;
        if (stockEl) stockEl.textContent = stockEl.dataset.srUnavailable || 'Unavailable';
        return;
      }

      if (idInput) {
        idInput.value = variant.id;
        idInput.disabled = !variant.available;
      }

      // Money strings are markup (the currency app wraps them in a span), so they
      // must be written as HTML, not text. And the node is left completely alone
      // when the amount has not changed — overwriting it would strip the currency
      // converter's own conversion in the common all-variants-same-price case.
      if (priceCurrent && priceCurrent.innerHTML.trim() !== variant.priceFormatted.trim()) {
        var sameAmount = priceCurrent.textContent.replace(/\s/g, '') ===
          variant.priceFormatted.replace(/<[^>]*>/g, '').replace(/\s/g, '');
        if (!sameAmount) priceCurrent.innerHTML = variant.priceFormatted;
      }

      var onSale = variant.compareAtPrice && variant.compareAtPrice > variant.price;
      if (priceCompare) {
        if (onSale && priceCompare.textContent.replace(/\s/g, '') !==
            variant.compareFormatted.replace(/<[^>]*>/g, '').replace(/\s/g, '')) {
          priceCompare.innerHTML = variant.compareFormatted;
        }
        priceCompare.hidden = !onSale;
      } else if (onSale && priceEl) {
        var s = document.createElement('s');
        s.setAttribute('data-sr-compare', '');
        s.innerHTML = variant.compareFormatted;
        priceEl.insertBefore(s, priceEl.firstChild);
        priceCompare = s;
      }
      if (saveFlag) saveFlag.hidden = !onSale;
      // The figure is pre-formatted per variant in Liquid, so no money maths
      // happens here and the currency app sees an ordinary money string.
      if (saveAmt && variant.saveFormatted &&
          saveAmt.innerHTML.trim() !== variant.saveFormatted.trim()) {
        saveAmt.innerHTML = variant.saveFormatted;
      }

      if (atc) atc.disabled = !variant.available;
      // Mirror rather than recompute, so the bar shows exactly what the rail
      // shows — including any conversion the currency app has applied.
      if (buybarPrice && priceCurrent) buybarPrice.innerHTML = priceCurrent.innerHTML;
      if (buybarAdd) buybarAdd.disabled = !variant.available;

      if (stockEl) {
        var dot = stockEl.querySelector('.sr-pdp__dot');
        if (dot) dot.classList.toggle('sr-pdp__dot--out', !variant.available);
        // The label is server-rendered for the initial variant; without this it
        // kept saying "In stock" after switching to a sold-out size.
        var label = variant.available
          ? stockEl.dataset.inStock
          : stockEl.dataset.outStock;
        if (label) {
          var text = document.createTextNode(label);
          while (stockEl.lastChild && stockEl.lastChild !== dot) stockEl.removeChild(stockEl.lastChild);
          stockEl.appendChild(text);
        }
      }

      setMedia(variant.mediaId);

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    root.addEventListener('change', function (e) {
      if (!e.target.matches('input[data-sr-option]')) return;
      update();
      // Collapse the size dropdown after a choice, the way a select behaves.
      var open = e.target.closest('[data-sr-select]');
      if (open) {
        open.open = false;
        var summary = open.querySelector('summary');
        if (summary) summary.focus();
      }
    });

    /* ---- Size dropdown: close on outside click / Escape ---- */
    document.addEventListener('click', function (e) {
      root.querySelectorAll('[data-sr-select][open]').forEach(function (sel) {
        if (!sel.contains(e.target)) sel.open = false;
      });
    });

    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = root.querySelector('[data-sr-select][open]');
      if (!open) return;
      open.open = false;
      var summary = open.querySelector('summary');
      if (summary) summary.focus();
    });

    /* ---- Size guide dialog ----
       <dialog>.showModal() gives focus trapping, Escape and the backdrop for free. */
    var guide = root.querySelector('[data-sr-guide]');
    if (guide) {
      var guideTrigger = null;

      root.querySelectorAll('[data-sr-guide-open]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          guideTrigger = btn;
          if (typeof guide.showModal === 'function') guide.showModal();
          else guide.setAttribute('open', '');
        });
      });

      guide.querySelectorAll('[data-sr-guide-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { guide.close(); });
      });

      // Click on the backdrop (outside the panel) closes it.
      guide.addEventListener('click', function (e) {
        if (e.target === guide) guide.close();
      });

      // A modal <dialog> is supposed to close itself on Escape, but something in
      // the theme swallows the key before the browser's cancel fires — so close it
      // explicitly. Captured at the document so it works wherever focus sits.
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && guide.open) {
          e.preventDefault();
          guide.close();
        }
      }, true);

      // Return focus to whatever opened it, however it was closed.
      guide.addEventListener('close', function () {
        if (guideTrigger && guideTrigger.focus) guideTrigger.focus();
      });
    }

    /* ---- Thumbnails ---- */
    root.querySelectorAll('[data-sr-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        setMedia(thumb.getAttribute('data-media-id'));
      });
    });

    /* ---- Quantity ----
       Horizon's ProductFormComponent#getQuantity() reads refs.quantitySelector,
       then falls back to its own data-quantity-default — it never looks at the
       form's `quantity` field. This custom stepper has no quantitySelector ref,
       so the attribute is mirrored on every change; without it every AJAX add
       silently posted a quantity of 1. */
    var qtyInput = root.querySelector('[data-sr-qty-input]');
    var productForm = root.querySelector('product-form-component');

    function syncQuantity() {
      if (!qtyInput || !productForm) return;
      var n = parseInt(qtyInput.value, 10);
      if (!n || n < 1) n = 1;
      qtyInput.value = n;
      productForm.dataset.quantityDefault = String(n);
    }

    if (qtyInput) {
      qtyInput.addEventListener('change', syncQuantity);
      qtyInput.addEventListener('input', syncQuantity);
      syncQuantity();
    }
    root.querySelectorAll('[data-sr-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var step = parseInt(btn.getAttribute('data-sr-qty'), 10);
        var next = (parseInt(qtyInput.value, 10) || 1) + step;
        qtyInput.value = next < 1 ? 1 : next;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    /* ---- Accordions: one open at a time ----
       <details> is independent by default, so opening a second would leave the
       first expanded. Closing the siblings on toggle keeps the rail compact and
       makes the open item unambiguous. Everything else — keyboard, semantics,
       the open state itself — stays native. */
    (function accordions() {
      var accs = Array.prototype.slice.call(root.querySelectorAll('.sr-acc'));
      if (accs.length < 2) return;
      accs.forEach(function (acc) {
        acc.addEventListener('toggle', function () {
          if (!acc.open) return;
          accs.forEach(function (other) {
            if (other !== acc) other.open = false;
          });
        });
      });
    })();

    /* ---- Trust benefits: 2-up auto carousel on mobile ----
       Scrolls the same track the desktop grid uses, so there is no duplicate
       markup. Auto-advance only runs below 768px, pauses while the visitor is
       interacting, and is skipped entirely for reduced-motion users. */
    (function trustCarousel() {
      var trust = root.querySelector('[data-sr-trust]');
      if (!trust) return;
      var track = trust.querySelector('[data-sr-trust-track]');
      if (!track) return;

      var items = track.querySelectorAll('.sr-trust__item');
      if (items.length < 3) return; // nothing to advance through

      var mobile = window.matchMedia('(max-width: 768px)');
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      var enabled = trust.dataset.autoplay === 'true';
      var delay = parseInt(trust.dataset.speed, 10) || 3500;
      var timer = null;
      var paused = false;

      function step() {
        // No document.hidden gate: browsers already throttle timers in background
        // tabs, and some in-app/embedded browsers report hidden while the page is
        // plainly visible — gating there would silently kill the autoplay.
        if (paused || !mobile.matches) return;
        var item = track.querySelector('.sr-trust__item');
        if (!item) return;
        var w = item.getBoundingClientRect().width;
        var max = track.scrollWidth - track.clientWidth;
        // Loop back to the start once the last pair has been shown.
        var next = track.scrollLeft >= max - 2 ? 0 : track.scrollLeft + w;
        // Assigning scrollLeft and letting CSS `scroll-behavior` do the easing is
        // more robust than scrollTo({behavior:'smooth'}), which silently does
        // nothing in environments where rAF is suspended.
        track.scrollLeft = next;
      }

      function start() {
        stop();
        if (!enabled || reduce.matches || !mobile.matches) return;
        timer = setInterval(step, delay);
      }

      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }

      // Let a visitor's own swipe win, then resume. Every pause is bounded by a
      // timeout that clears it, so a missed pointerup/mouseleave (common on touch,
      // and when a cursor is left parked over the section) can never latch the
      // carousel off permanently.
      var resume;
      function pauseFor(ms) {
        paused = true;
        clearTimeout(resume);
        resume = setTimeout(function () { paused = false; }, ms);
      }

      ['pointerdown', 'touchstart', 'wheel'].forEach(function (evt) {
        track.addEventListener(evt, function () { pauseFor(4000); }, { passive: true });
      });

      ['pointerup', 'touchend'].forEach(function (evt) {
        track.addEventListener(evt, function () { pauseFor(2500); }, { passive: true });
      });

      // Hover-pause only where hovering is a real input, and still time-bounded.
      if (window.matchMedia('(hover: hover)').matches) {
        trust.addEventListener('mouseenter', function () { pauseFor(6000); });
        trust.addEventListener('mouseleave', function () {
          clearTimeout(resume);
          paused = false;
        });
      }

      track.addEventListener('focusin', function () { pauseFor(6000); });
      track.addEventListener('focusout', function () {
        clearTimeout(resume);
        paused = false;
      });

      mobile.addEventListener('change', start);
      reduce.addEventListener('change', start);

      start();
    })();

    /* ---- Mobile buy bar ----------------------------------------------------
       Shown only once the real Add to cart has scrolled out of view, and hidden
       again over the footer so it never covers it. The button is a proxy: it
       clicks the actual Add to cart, so the form, the variant and the cart
       behaviour are all the existing ones. */
    (function () {
      if (!buybar || !atc || !buybarAdd) return;

      var footer = document.querySelector('.sr-footer') ||
        document.querySelector('.shopify-section:last-of-type');
      var visible = false;

      function apply() {
        // Above the breakpoint the bar is display:none, so skip the work.
        // 1024 matches the CSS: it is where the grid stops being two columns.
        if (window.innerWidth > 1024) {
          if (visible) { visible = false; buybar.classList.remove('is-visible'); }
          return;
        }
        var past = atc.getBoundingClientRect().bottom < 0;
        var atFooter = footer
          ? footer.getBoundingClientRect().top < window.innerHeight
          : false;
        var next = past && !atFooter;
        if (next === visible) return;
        visible = next;
        buybar.classList.toggle('is-visible', visible);
      }

      // Horizon makes .page-wrapper the scroll container at desktop widths, so
      // scroll events do not reach window from it — capture catches both cases.
      document.addEventListener('scroll', apply, { capture: true, passive: true });
      window.addEventListener('resize', apply, { passive: true });

      buybarAdd.addEventListener('click', function () {
        if (atc.disabled) return;
        atc.click();
      });

      if (buybarPrice && priceCurrent) buybarPrice.innerHTML = priceCurrent.innerHTML;
      buybar.removeAttribute('hidden');
      apply();
    })();

    /* ---- Rating jumps to the reviews --------------------------------------
       The widget is an app block with no id of ours, so it is located by its
       own container rather than by an anchor we would have to inject. */
    (function () {
      var jump = root.querySelector('[data-sr-reviews-jump]');
      if (!jump) return;
      jump.addEventListener('click', function () {
        var target = document.querySelector('.jm-review-widget') ||
          document.querySelector('.jdgm-widget');
        if (!target) return;
        var section = target.closest('.shopify-section') || target;
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    })();

    // Reflect the server-rendered state without rewriting it on load.
    syncActiveClasses();
    markAvailability();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
